import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useData } from '../../context/DataContext';
import { usePerfil } from '../../context/PerfilContext';
import { formatoEuros, formatoPorcentaje } from '../../utils/numeros';
import {
  Periodo, comercios, comprometidoAlMes, fijosYVariables, gastoPorDiaDelMes, gastoPorDiaSemana, indiceDia,
  mayoresGastos, periodoAnio, periodoMes,
} from '../../utils/informes';
import { BloquePlus, EtiquetaPlus, TablaDatos, Tarjeta, Vacio, VistaInforme, useColoresGrafico } from './comun';

/** I5: los gastos más grandes y lo que más se repite. */
const MayoresGastos: React.FC<{ periodo: Periodo }> = ({ periodo }) => {
  const { transacciones, categorias } = useData();
  const top = useMemo(() => mayoresGastos(transacciones, periodo), [transacciones, periodo]);
  const repetidos = useMemo(() => comercios(transacciones, periodo), [transacciones, periodo]);
  const cat = (id: string) => categorias.find(c => c.id === id);

  return (
    <>
      <Tarjeta titulo="Los gastos más grandes" subtitulo="Los 10 movimientos de más importe del periodo.">
        {top.length === 0 ? <Vacio>No hay gastos en este periodo.</Vacio> : (
          <ol className="divide-y divide-gray-100 dark:divide-gray-700">
            {top.map((t, i) => (
              <li key={t.id} className="flex items-center gap-3 py-2">
                <span className="text-xs text-gray-400 w-4 text-right tabular-nums">{i + 1}</span>
                <span className="shrink-0">{cat(t.categoriaId)?.icono ?? '💸'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">{t.descripcion || cat(t.categoriaId)?.nombre || 'Sin descripción'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{format(new Date(t.fecha), "d 'de' MMM", { locale: es })} · {cat(t.categoriaId)?.nombre ?? 'Sin categoría'}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums text-red-600 dark:text-red-400">{formatoEuros(t.cantidad)}</span>
              </li>
            ))}
          </ol>
        )}
      </Tarjeta>

      <Tarjeta titulo="Dónde repites" subtitulo="Descripciones que se repiten, por lo gastado en total. Escribe siempre igual el sitio (Mercadona, gasolinera…) para que se agrupe.">
        {repetidos.length === 0 ? <Vacio>No hay gastos con la misma descripción repetida.</Vacio> : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {repetidos.map(c => (
              <li key={c.nombre} className="flex items-center gap-3 py-2 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="truncate text-gray-900 dark:text-white">{c.nombre}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{c.veces} veces · {formatoEuros(c.total / c.veces)} de media</p>
                </div>
                <span className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatoEuros(c.total)}</span>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </>
  );
};

/** I9: qué parte del gasto viene de periódicos (comprometida de antemano). */
const FijosVariables: React.FC<{ periodo: Periodo }> = ({ periodo }) => {
  const { transacciones, transaccionesRecurrentes } = useData();
  const r = useMemo(() => fijosYVariables(transacciones, transaccionesRecurrentes, periodo), [transacciones, transaccionesRecurrentes, periodo]);
  const comprometido = comprometidoAlMes(transaccionesRecurrentes, 'gasto');
  const entra = comprometidoAlMes(transaccionesRecurrentes, 'ingreso');
  const total = r.fijos + r.variables;

  return (
    <Tarjeta titulo="Gastos fijos y variables" subtitulo="Fijos son los que salen de un periódico (alquiler, recibos, suscripciones…)." accion={<EtiquetaPlus />}>
      {total === 0 ? <Vacio>No hay gastos en este periodo.</Vacio> : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden gap-0.5 bg-gray-100 dark:bg-gray-700">
            <div className="bg-gray-600 dark:bg-gray-300" style={{ width: `${(r.fijos / total) * 100}%` }} title={`Fijos: ${formatoEuros(r.fijos)}`} />
            <div className="flex-1" style={{ backgroundColor: '#C8584A' }} title={`Variables: ${formatoEuros(r.variables)}`} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
            <div>
              <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"><span className="w-2.5 h-2.5 rounded-sm bg-gray-600 dark:bg-gray-300" /> Fijos</p>
              <p className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatoEuros(r.fijos)} <span className="text-xs text-gray-400">{r.porcentajeFijos !== null && formatoPorcentaje(r.porcentajeFijos)}</span></p>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#C8584A' }} /> Variables</p>
              <p className="font-semibold tabular-nums text-gray-900 dark:text-white">{formatoEuros(r.variables)} <span className="text-xs text-gray-400">{r.porcentajeFijos !== null && formatoPorcentaje(100 - r.porcentajeFijos)}</span></p>
            </div>
          </div>
        </>
      )}
      {comprometido > 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
          Con tus periódicos activos tienes comprometidos <span className="font-semibold tabular-nums">{formatoEuros(comprometido)}</span> al mes
          {entra > 0 && <> y te entran <span className="font-semibold tabular-nums">{formatoEuros(entra)}</span></>}.
        </p>
      )}
    </Tarjeta>
  );
};

/** I11: qué días de la semana se gasta más y, en un mes, el calendario día a día. */
const DiasSemana: React.FC<{ periodo: Periodo; vista: VistaInforme }> = ({ periodo, vista }) => {
  const { transacciones } = useData();
  const c = useColoresGrafico();
  const dias = useMemo(() => gastoPorDiaSemana(transacciones, periodo), [transacciones, periodo]);
  const max = Math.max(...dias.map(d => d.media), 0.01);
  const masCaro = dias.reduce((a, b) => (b.media > a.media ? b : a), dias[0]);
  const delMes = useMemo(
    () => (vista.mes === null ? null : gastoPorDiaDelMes(transacciones, vista.anio, vista.mes)),
    [transacciones, vista.anio, vista.mes],
  );
  const maxDia = delMes ? Math.max(...delMes, 0.01) : 1;
  const hueco = vista.mes === null ? 0 : indiceDia(new Date(vista.anio, vista.mes - 1, 1));

  return (
    <Tarjeta titulo="Qué días gastas más" subtitulo="Gasto medio de cada día de la semana en el periodo." accion={<EtiquetaPlus />}>
      {masCaro.media === 0 ? <Vacio>No hay gastos en este periodo.</Vacio> : (
        <>
          <ul className="space-y-1.5">
            {dias.map(d => (
              <li key={d.dia} className="flex items-center gap-2 text-sm">
                <span className="w-20 shrink-0 text-gray-600 dark:text-gray-300">{d.dia}</span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
                  <div className="h-2.5 rounded-full" style={{ width: `${(d.media / max) * 100}%`, backgroundColor: c.gastos }} />
                </div>
                <span className="w-24 text-right tabular-nums text-gray-900 dark:text-white">{formatoEuros(d.media)}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
            El día que más gastas es el <span className="font-semibold lowercase">{masCaro.dia}</span>: {formatoEuros(masCaro.media)} de media.
          </p>

          {delMes && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">Día a día</p>
              <div className="grid grid-cols-7 gap-1 text-center">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(l => <span key={l} className="text-[10px] text-gray-400">{l}</span>)}
                {Array.from({ length: hueco }).map((_, i) => <span key={`h${i}`} />)}
                {delMes.map((v, i) => {
                  const intensidad = v > 0 ? 0.15 + 0.75 * (v / maxDia) : 0;
                  return (
                    <div key={i} title={`${i + 1}: ${formatoEuros(v)}`}
                      className="aspect-square rounded-md flex items-center justify-center text-[11px] border border-gray-100 dark:border-gray-700"
                      style={{ backgroundColor: v > 0 ? `rgba(200, 88, 74, ${intensidad})` : undefined }}>
                      <span className={intensidad > 0.6 ? 'text-white font-semibold' : 'text-gray-600 dark:text-gray-300'}>{i + 1}</span>
                    </div>
                  );
                })}
              </div>
              <TablaDatos
                cabecera={['Día', 'Gasto']}
                filas={delMes.map((v, i) => [String(i + 1), formatoEuros(v)]).filter(f => f[1] !== formatoEuros(0))}
              />
            </div>
          )}
        </>
      )}
    </Tarjeta>
  );
};

export const SeccionHabitos: React.FC<{ vista: VistaInforme }> = ({ vista }) => {
  const { esPlus } = usePerfil();
  const periodo = useMemo(
    () => (vista.mes === null ? periodoAnio(vista.anio) : periodoMes(vista.anio, vista.mes)),
    [vista.anio, vista.mes],
  );
  return (
    <div className="space-y-4">
      <MayoresGastos periodo={periodo} />
      {esPlus ? (
        <>
          <FijosVariables periodo={periodo} />
          <DiasSemana periodo={periodo} vista={vista} />
        </>
      ) : (
        <>
          <BloquePlus titulo="Gastos fijos y variables" texto="Qué parte de lo que gastas está comprometida de antemano con tus periódicos y cuánto es gasto del día a día." />
          <BloquePlus titulo="Qué días gastas más" texto="Tu gasto medio de cada día de la semana y un calendario del mes para ver los días más caros." />
        </>
      )}
    </div>
  );
};
